import React, { useState, useEffect, useCallback } from 'react';
import { History } from 'lucide-react';
import { AuditLog } from '../../types/audit';
import { auditService } from '../../services/auditService';
import { Card, EmptyState, Skeleton } from '../ui';
import { ACTION_DOT, ActionPill, AuditDetails, auditActionTone, entityTypeLabel, formatShortDateTime } from './auditFormat';

interface EntityAuditTrailProps {
  entityType: string;
  entityId: string;
  className?: string;
}

export const EntityAuditTrail: React.FC<EntityAuditTrailProps> = ({
  entityType,
  entityId,
  className = '',
}) => {
  const [auditTrail, setAuditTrail] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAuditTrail = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await auditService.getEntityAuditTrail(entityType, entityId);
      setAuditTrail(response.auditTrail);
    } catch (err) {
      setError('Failed to load audit trail');
      console.error('Error loading audit trail:', err);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    loadAuditTrail();
  }, [loadAuditTrail]);

  const header = (
    <div className="flex min-h-[48px] items-center justify-between gap-3 border-b border-neutral-200/70 px-4 py-2.5">
      <h3 className="min-w-0 text-[15px] font-normal tracking-[-0.01em] text-ink">
        {entityTypeLabel(entityType)}{' '}
        <span className="break-all font-ui-mono text-[12px] text-neutral-400">{entityId}</span>
      </h3>
      {!loading && !error && auditTrail.length > 0 && (
        <span className="flex-shrink-0 text-[12px] tabular-nums text-neutral-400">
          {auditTrail.length} {auditTrail.length === 1 ? 'event' : 'events'}
        </span>
      )}
    </div>
  );

  if (loading) {
    return (
      <Card className={className}>
        {header}
        <div data-testid="loading-spinner" className="space-y-4 px-4 py-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-start gap-3">
              <Skeleton className="mt-1 h-2.5 w-2.5 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-3.5 w-52" />
                <Skeleton className="h-3 w-28" />
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        {header}
        <div className="p-4">
          <p role="alert" className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
            {error}
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className={className}>
      {header}

      {auditTrail.length === 0 ? (
        <EmptyState icon={History} title="No history" />
      ) : (
        <ol className="px-4 py-3">
          {auditTrail.map((log, index) => {
            const isLast = index === auditTrail.length - 1;

            return (
              <li key={log.id} className="relative flex gap-3 pb-4 last:pb-1">
                {/* Hairline connector */}
                {!isLast && (
                  <span className="absolute -bottom-[7px] left-[4.5px] top-[18px] w-px bg-neutral-200" aria-hidden="true" />
                )}
                <span
                  className={`relative mt-[7px] h-2.5 w-2.5 flex-shrink-0 rounded-full ring-4 ring-white ${
                    ACTION_DOT[auditActionTone(log.action)]
                  }`}
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <p className="min-w-0 text-[13px] leading-6 text-ink">
                      <span className="break-all">{log.user?.email ?? 'System'}</span>{' '}
                      <ActionPill action={log.action} />
                    </p>
                    <time
                      dateTime={log.createdAt}
                      title={new Date(log.createdAt).toLocaleString()}
                      className="flex-shrink-0 text-[12px] leading-6 tabular-nums text-neutral-400"
                    >
                      {formatShortDateTime(log.createdAt)}
                    </time>
                  </div>
                  {log.details && (
                    <AuditDetails details={log.details} className="mt-1" />
                  )}
                  {log.ipAddress && (
                    <p className="mt-1 font-ui-mono text-[11.5px] text-neutral-400">{log.ipAddress}</p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
};
